import React, { useState } from "react";
import { Card, Row, Col, Badge, Button, Modal, Form } from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaRoute,
  FaClock,
  FaStore,
  FaUtensils,
  FaCheckCircle,
  FaPowerOff,
} from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { toast } from "react-toastify";

import "./OutletManager.css";
import { useDispatch, useSelector } from "react-redux";
import { updateOutletMenuItems } from "../actions/outletActions";
import { toggleOutletActivation } from "../actions/outletActions";
import OpenHoursManager from "./OpenHours/OpenHoursManager";
// The dish picker is shared with the admin Restaurants directory.
import OutletMenuItemsModal from "./OutletMenuItemsModal";
import ConfirmDialog, { useConfirmDialog } from "./ConfirmDialog";
import { useHeadOfficeGate, useLocationGate } from "./team/Gated";
import { branchAwareMessage } from "../utilities/vendorScope";
import { MY_BRANCHES_ONLY, NOT_YOUR_OUTLETS, useOutletsInScope } from "../hooks/useVendorAccess";
import {
  apiFetchOutletHours,
  apiUpdateOutletHours,
  apiDeleteOutletHours,
} from "../api/index";

/**
 * Branches (4 Oct 2026): an outlet's switch and hours are that branch's to
 * run (a member limited to some branches can run theirs and no other); what
 * an outlet sells is head office's. `onChanged` refetches the restaurant
 * after an hours change, instead of reloading the whole page.
 */
const OutletManager = ({ restaurantId, sections, readOnly = false, onChanged }) => {
  const dispatch = useDispatch();

  // Get restaurant from Redux state for reactive updates
  const restaurantFromRedux = useSelector((state) =>
    state.restaurants.restaurants.find((r) => r._id === restaurantId)
  );

  // Merge sections from props with restaurant from Redux
  const restaurant = {
    ...restaurantFromRedux,
    sections: sections || restaurantFromRedux?.sections,
  };

  const outlets = restaurant?.outlets || [];
  // What this login runs: a branch member sees their own outlets only. The
  // menu save below still writes against the full list.
  const { outlets: shownOutlets, limited } = useOutletsInScope(restaurant?.outlets);
  const [showMenuItemsModal, setShowMenuItemsModal] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  // Business-hours save in flight. The dish picker has its own saving state
  // inside OutletMenuItemsModal; the two used to share one flag.
  const [isSaving, setIsSaving] = useState(false);
  // Vendor team permissions: outlet toggles, menu items and hours are
  // outlets:manage (components/team/Gated.js), at the outlet for the switch
  // and the hours, head office only for the menu.
  const outletAt = useLocationGate("outlets:manage");
  const menuGate = useHeadOfficeGate("outlets:manage");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [outletToToggle, setOutletToToggle] = useState(null);
  const [showBusinessHoursModal, setShowBusinessHoursModal] = useState(false);
  const [selectedOutletForHours, setSelectedOutletForHours] = useState(null);
  const [outletHours, setOutletHours] = useState(null);
  const [isLoadingHours, setIsLoadingHours] = useState(false);

  const handleManageMenuItems = (outlet) => {
    setSelectedOutlet(outlet);
    setShowMenuItemsModal(true);
  };

  // Persist through the outlet redux action (it also updates the store); the
  // shared modal owns the selection and closes itself on success.
  // The action toasts the outcome once and rethrows, which keeps the modal
  // open with the selection intact.
  const handleSaveMenuItems = async (menuItemIds) => {
    await dispatch(updateOutletMenuItems(selectedOutlet._id, menuItemIds, { vertical: "restaurant" }));
    const updatedOutlets = outlets.map((outlet) =>
      outlet._id === selectedOutlet._id
        ? { ...outlet, menuItems: menuItemIds }
        : outlet
    );
    if (restaurant && restaurant.outlets) {
      restaurant.outlets = updatedOutlets;
    }
  };

  const handleToggleActivation = (outlet) => {
    setOutletToToggle(outlet);
    setShowConfirmModal(true);
  };

  const confirmToggleActivation = async () => {
    if (!outletToToggle) return;

    const newStatus = !outletToToggle.isActive;
    try {
      // Toasts the outcome itself.
      await dispatch(toggleOutletActivation(outletToToggle._id, newStatus, { vertical: "restaurant" }));
    } catch (error) {
      console.error("Error toggling outlet activation:", error);
    } finally {
      setShowConfirmModal(false);
      setOutletToToggle(null);
    }
  };

  const handleManageBusinessHours = async (outlet) => {
    setSelectedOutletForHours(outlet);
    setShowBusinessHoursModal(true);
    setIsLoadingHours(true);

    try {
      // Fetch outlet-specific hours or inherit from parent
      const response = await apiFetchOutletHours(outlet._id);
      const hours = response.data.openHours || restaurant.openHours || null;
      setOutletHours(hours);
    } catch (error) {
      console.error("Error fetching outlet hours:", error);
      // If outlet doesn't have custom hours, use parent's hours
      setOutletHours(restaurant.openHours || null);
    } finally {
      setIsLoadingHours(false);
    }
  };

  const handleSaveOutletHours = async (hoursData) => {
    if (!selectedOutletForHours) return;

    setIsSaving(true);
    try {
      await apiUpdateOutletHours(selectedOutletForHours._id, hoursData);
      toast.success(
        `Business hours updated for ${selectedOutletForHours.name}!`
      );
      setShowBusinessHoursModal(false);
      if (typeof onChanged === "function") onChanged();
    } catch (error) {
      console.error("Error saving outlet hours:", error);
      toast.error(branchAwareMessage(error, "Failed to update business hours"));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDialog = useConfirmDialog();

  const handleRemoveCustomHours = () => {
    if (!selectedOutletForHours) return;
    const outlet = selectedOutletForHours;
    confirmDialog.ask({
      title: `Remove custom hours for ${outlet.name}?`,
      body: "The outlet goes back to inheriting the parent restaurant's hours.",
      tone: "warning",
      confirmLabel: "Remove custom hours",
      busyLabel: "Removing...",
      onConfirm: async () => {
        try {
          await apiDeleteOutletHours(outlet._id);
          toast.success(
            `Custom hours removed. ${outlet.name} now inherits parent hours.`
          );
          setShowBusinessHoursModal(false);
          if (typeof onChanged === "function") onChanged();
        } catch (error) {
          console.error("Error removing custom hours:", error);
          toast.error(branchAwareMessage(error, "Failed to remove custom hours"));
        }
      },
    });
  };

  return (
    <>
      <ConfirmDialog {...confirmDialog.props} />
      <Card className="mt-3 shadow-lg">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
          <div>
            <h4 className="mb-0">
              <FaStore className="me-2 text-primary" />
              {restaurant.name} - Outlets
            </h4>
            <small className="text-muted">
              {limited
                ? MY_BRANCHES_ONLY
                : `Managing ${outlets.length} outlet${outlets.length !== 1 ? "s" : ""}`}
            </small>
          </div>
        </Card.Header>
        <Card.Body className="bg-light">
          <Row className="g-4">
            {shownOutlets.length > 0 ? (
              shownOutlets.map((outlet) => (
                <Col md={4} key={outlet._id}>
                  <Card className="h-100 border-1 shadow-sm hover-shadow">
                    <Card.Body>
                      <Card.Title className="d-flex justify-content-between align-items-center mb-3">
                        <span className="outlet-name">{outlet.name}</span>
                        <Form.Check
                          type="switch"
                          id={`outlet-switch-${outlet._id}`}
                          checked={outlet.isActive}
                          onChange={() => handleToggleActivation(outlet)}
                          className="outlet-switch"
                          disabled={isSaving || !outletAt(outlet._id).allowed}
                          title={outletAt(outlet._id).title}
                        />
                      </Card.Title>
                      <div className="outlet-details">
                        <div className="mb-2 d-flex align-items-center text-muted">
                          <FaMapMarkerAlt className="me-2 text-danger" />
                          <span className="small">{outlet.address}</span>
                        </div>
                        <div className="mb-2 d-flex align-items-center text-muted">
                          <FaRoute className="me-2 text-success" />
                          <span className="small">
                            Delivery Radius: {outlet.deliveryZoneRadius} km
                          </span>
                        </div>
                        <div className="mb-2 d-flex align-items-center text-muted">
                          <FaUtensils className="me-2 text-warning" />
                          <span className="menu-items-count d-flex align-items-center gap-1">
                            <MdVerified className="check-icon" />
                            Menu Items:
                            <span className="count-badge">
                              {outlet.menuItems?.length || 0}
                            </span>
                          </span>
                        </div>
                        <div className="mb-2 d-flex align-items-center text-muted">
                          <FaClock className="me-2 text-info" />
                          <span className="small">
                            Added:{" "}
                            {new Date(outlet.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="mb-2 d-flex align-items-center">
                          <FaClock
                            className="me-2"
                            style={{
                              color: outlet.openHours ? "#17a2b8" : "#ffc107",
                            }}
                          />
                          {outlet.openHours ? (
                            <Badge bg="info" pill className="text-white">
                              Custom Hours
                            </Badge>
                          ) : (
                            <Badge bg="warning" pill className="text-dark">
                              Inherits Parent Hours
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <FaPowerOff
                            className={`me-2 ${
                              outlet.isActive ? "text-success" : "text-danger"
                            }`}
                          />
                          <Badge
                            bg={outlet.isActive ? "success" : "danger"}
                            className="status-badge"
                            pill
                          >
                            {outlet.isActive ? "Active" : "Deactivated"}
                          </Badge>
                        </div>
                      </div>
                    </Card.Body>
                    <Card.Footer className="bg-white border-0">
                      <div className="d-grid gap-2">
                        <Button
                          disabled={!menuGate.allowed}
                          title={menuGate.title}
                          variant="primary"
                          size="sm"
                          onClick={() => handleManageMenuItems(outlet)}
                          className="custom-manage-btn"
                        >
                          <FaUtensils className="me-2" />
                          Manage Menu Items
                        </Button>
                        <Button
                          disabled={!outletAt(outlet._id).allowed}
                          title={outletAt(outlet._id).title}
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleManageBusinessHours(outlet)}
                          className="custom-manage-btn"
                        >
                          <FaClock className="me-2" />
                          Manage Business Hours
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              ))
            ) : (
              <Col>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center py-5">
                    <FaStore className="text-muted mb-3" size={40} />
                    <h5 className="text-muted">No outlets available</h5>
                    <p className="text-muted mb-0">
                      {limited && outlets.length > 0
                        ? NOT_YOUR_OUTLETS
                        : `Contact support to add outlets for ${restaurant.name || "this restaurant"}`}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <OutletMenuItemsModal
        show={showMenuItemsModal}
        outlet={selectedOutlet}
        sections={restaurant.sections}
        onHide={() => setShowMenuItemsModal(false)}
        onSave={handleSaveMenuItems}
      />

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to{" "}
            {outletToToggle?.isActive ? "deactivate" : "activate"}{" "}
            {outletToToggle?.name}?
          </p>
          {outletToToggle?.isActive ? (
            <div className="alert-dialog-deactivate" role="alert">
              <small>
                ⚠️ Deactivating this outlet will make it unavailable for
                customers to place orders!
              </small>
            </div>
          ) : (
            <div className="alert-dialog-activate" role="alert">
              <small>
                ✅ Activating this outlet will make it available for customers
                to place orders!
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant={outletToToggle?.isActive ? "danger" : "success"}
            onClick={confirmToggleActivation}
          >
            {outletToToggle?.isActive ? "Deactivate" : "Activate"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Business Hours Management Modal */}
      <Modal
        show={showBusinessHoursModal}
        onHide={() => setShowBusinessHoursModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FaClock className="me-2 text-primary" />
            Manage Business Hours - {selectedOutletForHours?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOutletForHours && (
            <div>
              <div
                className={`alert ${
                  selectedOutletForHours.openHours
                    ? "alert-info"
                    : "alert-warning"
                } mb-3`}
              >
                <strong>
                  {selectedOutletForHours.openHours
                    ? "⚙️ Custom Hours Active"
                    : "🔗 Inheriting from Parent"}
                </strong>
                <p className="mb-0 mt-2">
                  {selectedOutletForHours.openHours ? (
                    <>
                      This outlet has custom business hours that override the
                      parent restaurant's hours. You can remove custom hours to
                      inherit from <strong>{restaurant.name}</strong>.
                    </>
                  ) : (
                    <>
                      This outlet currently inherits business hours from{" "}
                      <strong>{restaurant.name}</strong>. Set custom hours below
                      to override parent restaurant hours.
                    </>
                  )}
                </p>
                {selectedOutletForHours.openHours && (
                  <Button
                    disabled={!outletAt(selectedOutletForHours._id).allowed}
                    title={outletAt(selectedOutletForHours._id).title}
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRemoveCustomHours}
                    className="mt-2"
                  >
                    Remove Custom Hours (Inherit from Parent)
                  </Button>
                )}
              </div>

              {isLoadingHours ? (
                <div className="text-center py-5">
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Loading business hours...
                </div>
              ) : (
                <OpenHoursManager
                  openHours={outletHours}
                  location={selectedOutletForHours._id}
                  onSave={handleSaveOutletHours}
                  isLoading={isSaving}
                  // An outlet inherits its parent vendor's zone.
                  allowTimezoneEdit={false}
                  onValidationError={(errors) => {
                    toast.error("Please fix validation errors before saving");
                  }}
                />
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OutletManager;
